import {
  Fullscreen,
  History,
  Modal,
  Redirect,
} from "@shopify/app-bridge/actions"
import { Action as RedirectAction } from "@shopify/app-bridge/actions/Navigation/Redirect"
import { isShopifyMobile } from "@shopify/app-bridge-utils"
import {
  Frame,
  FullscreenBar,
  Loading,
  Toast as PolarisToast,
} from "@shopify/polaris"
import { AppProvider } from "@shopify/polaris"
import { Sentry } from "channelwill-sentry-sdk"
// Shopify polaris 组件的翻译文件
import classNames from "classnames"
import { ClickToComponent } from "click-to-react-component"
import { cloneDeep, isEmpty, startsWith } from "lodash-es"
import { Observer, observer, Provider } from "mobx-react"
import { createCrossSellTracker } from "parcelpanel-cross-sell-tracking"
import { ShopifyMenu } from "polaris-enhance"
import { ResourcePicker, UnsavedChangedBar } from "polaris-enhance"
import qs from "qs"
import React, {
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react"
import { PBProvide } from "react-pp-bundle"
import { matchRoutes, renderRoutes } from "react-router-config"
import { useHistory, useLocation } from "react-router-dom"

import APIApp from "@/api/app"
import { FullScreenLoading, Link, PostLayer } from "@/components"
import V6TrialEndBanner from "@/components/Banner/V6TrialEndBanner"
import ConfirmModalInPage from "@/components/ConfirmModal/ConfirmModalInPage"
import CustomTopBar from "@/components/CustomTopBar"
import { ExpertHelpPost } from "@/components/PostLayer/PostLayer"
import { CROSS_SELL_APP_KEY } from "@/enums/CrossSellPoint"
import { CustomFullscreenBar, WorkflowFullscreenBar } from "@/init/Entry"
import { setLocal } from "@/locales/config"
import Plan from "@/pages/Plan"
import routes from "@/router"
import stores from "@/stores"
import { ShopifyInitParams } from "@/types/ShopifyInitParams"
import { postRouteMessageToAdmin } from "@/utils/admin-post-message"
import AppBridge, { isReturnAdmin, isShopifyEmbedded } from "@/utils/app-bridge"
import AppBridgeHelper from "@/utils/app-bridge-helper"
import dayjs from "@/utils/dayjs"
import { isProduction, isShopifyEnv } from "@/utils/env-helper"
import { isDebugEnv } from "@/utils/extension-helper"
import { addCss, initIntercom } from "@/utils/intercom"
import { hasChosenPlan } from "@/utils/plan"
import toast from "@/utils/toast"

import { getLangTranslate } from "../utils"
import { CWComponentsProvider } from "./CWComponentsProvider"
import Onboading from "./Onboading/Onboading"

/**
 * 判断是本地还是嵌入式
 * @param {Component} 需要判断的路由组件
 * @return 新的包裹组件
 */
function BridgeTransfer() {
  const [isFullscreen, setIsFullscreen] = useState(false)
  const location = useLocation()
  const history = useHistory()
  useLayoutEffect(() => {
    const className = "shopify-mobile-viewport-offset"
    const shouldOffset = isShopifyEmbedded() && isShopifyMobile()

    document.body.classList.toggle(className, shouldOffset)
    return () => {
      document.body.classList.remove(className)
    }
  }, [])

  useEffect(() => {
    window.pnav = (path, state) => {
      history.push(path, state)
    }
    window.preplace = (path, state) => {
      history.replace(path, state)
    }
    window.pnavback = () => {
      history.goBack()
    }
  }, [history])
  const { pathname } = useLocation()
  const branch = matchRoutes(routes, pathname)

  // 忽略是否加载完用户数据
  const ignoreIsLoadedUserData = startsWith(pathname, "/")

  const requestAuthParam = qs.parse(location.search, {
    ignoreQueryPrefix: true,
    parseArrays: false,
  }) as ShopifyInitParams
  const { host }: ShopifyInitParams = requestAuthParam

  if (!window.returnParentHost && host) {
    window.returnParentHost = host
  }

  const {
    CommonStore,
    UserInfoStore,
    ReturnRequestsDataStore,
    ManagementStore,
    PlanStore,
    CacheStore,
  } = stores

  const handleAppBridgeRedirect = useCallback(
    (data) => {
      AppBridge.modal().dispatch(Modal.Action.CLOSE) // 路径变化需要取消弹窗
      history.push(data.path)
    },
    [history],
  )

  React.useLayoutEffect(() => {
    async function requestBaseAPI() {
      try {
        window.systemLanguage = requestAuthParam.locale || "en"
        let city = ""
        const [res] = await Promise.all([
          APIApp.shopAddressInfo(),
          UserInfoStore.updateUserInfo(),
        ])

        city = (res as any)?.data?.city || ""

        const data = cloneDeep(UserInfoStore.data)

        if (data?.jump_url) {
          // 后端返回安装逻辑 ，如果后端不知道 就会返回链接
          AppBridgeHelper.redirectRemoteDispatch(data?.jump_url)
          return
        }

        setLocal(data.language_admin)
        UserInfoStore.setUserGuideData(data.guide, true)
        window.cio?.init({
          id: data.shop,
          shop_owner: data.shop_owner,
          email: data.email,
          shop_name: data.shop_name,
          shop_domain: data.shop,
          shop_plan: data.shopify_plan?.displayName,
          shop_created_at: dayjs(data.created_at * 1000).format("YYYY-MM-DD"),
          app_name: "Returns",
          language: data.language_admin,
          shopify_plan: data.shopify_plan?.displayName || "",
          city: city,
          country_name: data.country_code,
        })
        window.cwreport?.init({
          api_key: import.meta.env.VITE_CWREPORT_API_KEY,
          app_id: import.meta.env.VITE_CWREPORT_APP_ID,
          test_mode: import.meta.env.VITE_CWREPORT_TEST_MODE === "true",
          gzip: import.meta.env.VITE_CWREPORT_GZIP === "true",
          debug: import.meta.env.VITE_CWREPORT_TEST_MODE === "true",
        })
        // 绑定店铺维度用户属性
        window.cwreport?.identify(data.shop, {
          shopify_domain: data.shop,
        })
        window.cwreport?.initPublicProperty({
          // 国家编码，示例：US
          country_code: data.country_code,
          // 城市，示例：Fort Lauderdale
          city: city,
          // shopify店铺类型，示例：Advanced
          shopify_plan: data.shopify_plan?.displayName ?? "",
          // app名称，示例：ParcelWILL Tracking
          app_name: "ParcelPanel Return",
          // 传递语言语种，示例：en
          language: data.language_admin,
        })
        window.cwreport?.report("app_start")
        window.track = (event, value) => {
          APIApp.track({
            event_name: event,
            value,
            package_kind: data.package_kind,
            router: window.__pathname,
          })
        }
        Sentry.setUser({
          id: data.shop, // 必填：用户唯一标识
          nickname: data.shop_name,
        })
        window.cio?.page({
          pathname: pathname,
          triggerIO: true,
        })

        // 创建主应用tracker实例
        const Tracker = createCrossSellTracker({
          apiKey: CROSS_SELL_APP_KEY.RETURN,
          environment: isProduction ? "production" : "test",
          enableLogging: true,
        })

        const redirectUrl = data.oauth
        // oauth 如果没有授权，跳转到首页页面
        if (!isEmpty(redirectUrl) && isShopifyEmbedded()) {
          const redirect = AppBridge.redirect()

          redirect.dispatch(RedirectAction.REMOTE, { url: redirectUrl })
          return
        }

        // console.table(flatten(data))

        // 后续接入intercom需要再打开
        if (
          // 不是从超管后台进入的，加载 intercom
          window.ManageParams.from !== "admin" ||
          // 从超管后台进入的，并且 display_intercom 为 true，加载 intercom
          (window.ManageParams.from === "admin" &&
            window.ManageParams.display_intercom === "true")
        ) {
          initIntercom(data)
        }

        // await stores.UserInfoStore.updateGuideInfo()

        if (!data.is_forbidden_visit) {
          window?.hideLoadingState()
          CommonStore.setFullScreenLoading(false)
        }

        // 授权支付都完成了，在这里处理其他业务

        await Promise.all([
          stores.CommonStore.updateAuthority(),
          stores.PlanStore.updatePlanData(),
        ])

        // 后端说换套餐后设置source可能没那么块，要我们自己做个判断，
        // 如果url有source参数，则将PlanStore.config.user_pkg.source设置为source
        if (requestAuthParam.source && PlanStore.config) {
          PlanStore.config.user_pkg.source = +requestAuthParam.source
        }

        // 获取店铺套餐权限

        // 套餐类型从1开始
        const map = ["", "Free", "Essential", "Professional", "Enterprise"]
        UserInfoStore.setData(data)
        // 初始化主应用
        Tracker.initApp({
          user_id: Number(data.id),
          shopify_id: Number(data.shop_id),
          domain: data.shop,
          plan: `${map[PlanStore.config?.user_pkg.package_kind ?? 0]}(${
            PlanStore.config?.user_pkg.package_period === 1
              ? "Monthly"
              : "Annually"
          })`,
          shopify_version: data.shopify_plan?.displayName || "",
          price: (PlanStore.completedCurPlan?.origin_price ?? 0).toFixed(2),
        }).then(() => {
          // 将tracker实例绑定到window对象，方便全局访问
          if (typeof window !== "undefined") {
            window.CrossSellTracker = Tracker
          }
        })

        UserInfoStore.setDataLoadedTime(dayjs().unix())

        CacheStore.getLanguageList()

        // 从超管后台进去不做显示
        if (window.ManageParams.from !== "admin") {
          APIApp.survey().then(({ data }) => {
            const { code, data: surveyInfo } = data || {}
            const {
              show_survey,
              external_user_id,
              client_id,
              sid,
              channel_id,
              width,
              parameters,
            } = surveyInfo || {}
            // 判断是否展示
            if (code !== 1000 || !show_survey) {
              return
            }
            // @ts-ignore
            window?.surveylite?.initSurvey({
              container: "#sv-builtin-container", // 挂载节点
              clientId: client_id, // 唯一编码， 当clientId传入值为客户编号，那么该问卷该客户只能填答一次
              externalUserId: external_user_id,
              sid: sid, // 问卷ID
              channelId: channel_id, // 渠道ID
              width: width,
              parameters: parameters, // 自定义参数
              env: "https://g.xmplus.cn",
              animation: true,
              // 关闭事件
              onClose: () => {
                APIApp.surveyMarkAction(2)
              },
              // 提交事件
              onSubmit: () => {
                APIApp.surveyMarkAction(1)
              },
            })
          })
        }
      } catch (e) {
        isDebugEnv() && CommonStore.setFullScreenLoading(false)
      } finally {
        // 如果店铺被禁用，展示 loading 效果 true表示禁用
        if (!UserInfoStore.data.is_forbidden_visit) {
          window?.hideLoadingState()
          CommonStore.setFullScreenLoading(false)
        }
      }
    }

    requestBaseAPI()

    // 如果不是在 Shopify 打开的
    if (!isShopifyEmbedded()) {
      return
    }

    AppBridge.app().subscribe(Redirect.Action.APP, handleAppBridgeRedirect)
  }, [])

  // async function fetchUserGuide() {
  //   try {
  //     const { data: { data } } = await APIApp.userGuide()

  //     stores.UserInfoStore.setUserGuideData(data)
  //   } catch (error) { /* empty */ } finally { /* empty */ }
  // }

  useEffect(() => {
    window.cio?.page({
      pathname: pathname,
    })
    window.__pathname = pathname
  }, [pathname])

  useEffect(() => {
    // 如果首页, 第一次加载quickSetupData 用户引导步骤信息
    // if (pathname === "/" && !stores.UserInfoStore.quickSetupInitialized){
    //   fetchUserGuide()
    // }
    // 如果不在management页面 清除session保存的查询条件
    if (!pathname.startsWith("/return-management")) {
      sessionStorage.removeItem("query")
    }
    // 引导订阅页面（在选择订阅套餐页面），切换其他菜单，增加toast提示语
    if (isShopifyEmbedded()) {
      const finishStep = UserInfoStore.guideInfo.finish_step
      const shouldSkipToast =
        finishStep === 4 && !["/Dashboard", "/"].includes(pathname)
      if (shouldSkipToast) {
        toast(t("PleaseSelectAPlanThatMatchesYourB"), 3000)
      }
    }
  }, [pathname])

  React.useEffect(() => {
    // 向父 iframe 发送路由消息
    isReturnAdmin() && postRouteMessageToAdmin(pathname)

    if (isShopifyEmbedded()) {
      History.create(AppBridge.app()).dispatch(History.Action.REPLACE, pathname)
    }

    // 需要全屏的页面，数组项为路由的key值
    if (
      [
        "EmailEdit", // 邮箱编辑
        "ReturnPageLayoutConfiguration", // return-page 全屏配置
        "LanguageEdit", // 语言编辑
        "workflow", // 工作流
        "FreeReturnserviceLayoutConfiguration", // free-return-service 全屏配置
        "AutomationLayoutConfigurationAutoApprove", // 自动审批
        "AutomationLayoutConfigurationAutoRefund", // 自动退款
        "AutomationLayoutConfigurationAutoExchange", // 自动换货
        "AutomationLayoutConfigurationSpecificRules", // 特定规则
      ].includes((branch[0].route.key || "") as string)
    ) {
      setIsFullscreen(true)
      isShopifyEmbedded() &&
        AppBridge.dispatchFullscreen(Fullscreen.Action.ENTER)
    } else {
      setIsFullscreen(false)
      isShopifyEmbedded() &&
        AppBridge.dispatchFullscreen(Fullscreen.Action.EXIT)
    }
    if (
      ReturnRequestsDataStore.analyticsType === 1 &&
      !pathname.includes("/analytics")
    ) {
      ReturnRequestsDataStore.setAnalyticsType(0)
    }
    if (!pathname.includes("/return-management")) {
      ManagementStore.resetListQueryDefaults()
    }
  }, [branch, pathname])

  useEffect(() => {
    window.scrollTo({
      behavior: "auto",
      left: 0,
      top: 0,
    })
  }, [pathname])

  if (!isShopifyEmbedded()) {
    addCss(
      "#root #return-page .Polaris-PositionedOverlay {transform: translate(-240px, -3.5rem);}",
    )
  }

  // 如果切换页面首页的评论引导弹窗关掉，防止纸屑动画影响性能
  useEffect(() => {
    if (ReturnRequestsDataStore.show_praise_guide_modal) {
      ReturnRequestsDataStore.setShowPraiseGuideModal(false)
    }
  }, [pathname])

  const jumpFeaturedApp = () => {
    const urlParams = new URLSearchParams(window.location.search)
    const jumpLink = urlParams.get("action")
    const _search = window.location.search
    if (jumpLink === "channelwill") {
      history.push(`/integration${_search}&tabsSelected=1`)
    }
  }

  useEffect(() => {
    // 判断中台集合页支付成功后跳转到中台集合页
    jumpFeaturedApp()
  }, [])

  const [i18n, setI18n] = useState<null | any>(null)
  useEffect(() => {
    getLangTranslate(UserInfoStore.data.language_admin).then((res) => {
      setI18n(res)
    })
  }, [UserInfoStore.data.language_admin])

  const hasChosenPlanFlag = hasChosenPlan()
  const shouldForcePlanPage = useMemo(() => {
    // @todo 临时通过
    // return false
    const userPkg = PlanStore.config?.user_pkg
    const isTrialActive =
      (userPkg?.rest_trial_days ?? 0) > 0 || (userPkg?.rest_seconds ?? 0) > 0
    return !hasChosenPlanFlag && !isTrialActive
  }, [PlanStore.config?.user_pkg, hasChosenPlanFlag])

  const topBar = useMemo(() => {
    if (isFullscreen) {
      if (pathname.includes("/workflow")) {
        return <WorkflowFullscreenBar />
      }
      if (pathname.includes("/return-page/configuration")) {
        return <CustomFullscreenBar />
      }
      if (
        [
          "FreeReturnserviceLayoutConfiguration", // free-return-service 全屏配置
          "AutomationLayoutConfigurationAutoApprove", // 自动审批
          "AutomationLayoutConfigurationAutoRefund", // 自动退款
          "AutomationLayoutConfigurationAutoExchange", // 自动换货
          "AutomationLayoutConfigurationSpecificRules", // 特定规则
        ].includes((branch[0].route.key || "") as string)
      ) {
        return (
          !isShopifyEnv && (
            <FullscreenBar
              onAction={() => {
                pnavback()
              }}
            />
          )
        )
      }
    } else {
      if (!isShopifyEmbedded()) {
        return <CustomTopBar />
      }
    }
    return null
  }, [history, isFullscreen, pathname])

  {
    /* 使用 linkComponent 属性的目的是替换为 react-router-dom 的 NotFound 组件实现无刷新跳转页面 */
  }
  return (
    <AppProvider i18n={i18n} linkComponent={Link}>
      <ResourcePicker />
      <Provider {...stores}>
        <Observer>
          {() => (
            <>
              <CWComponentsProvider />
              <PBProvide language={UserInfoStore.data.language_admin}>
                {isShopifyEmbedded() ? (
                  <ShopifyMenu
                    menus={[
                      {
                        title: t("Dashboard"),
                        path: "/",
                        rel: "home",
                      },
                      {
                        title: t("ManageReturns"),
                        path: "/return-management",
                      },
                      {
                        title: t("ReturnPortal"),
                        path: "/return-page",
                      },
                      {
                        title: t("Settings"),
                        path: "/settings",
                      },
                      {
                        title: t("Protection"),
                        path: "/free-return-service",
                      },
                      {
                        title: t("Analytics"),
                        path: "/analytics",
                      },
                      {
                        title: t("Integration"),
                        path: "/integration",
                      },
                      {
                        title: t("Billing"),
                        path: "/billing",
                      },
                      ...(import.meta.env.VITE_APP_ENV === "local"
                        ? [
                            {
                              title: t("DemoPage"),
                              path: "/demo-page",
                            },
                          ]
                        : []),
                    ]}
                  />
                ) : null}
                {CommonStore.fullScreenLoading || !ignoreIsLoadedUserData ? (
                  <FullScreenLoading />
                ) : (
                  <div
                    className={classNames({
                      "return-container--fullscreen": isFullscreen,
                      "return-container--shopify-env": isShopifyEnv,
                      "return-container--no-shopify-env": !isShopifyEnv,
                    })}
                  >
                    <Frame topBar={topBar} navigation={null}>
                      {/* fallback={<FullScreenLoading />} 去掉loading设置 */}
                      <Suspense>
                        {/* 用于修复intercom挡住问题 */}
                        <div>
                          <UnsavedChangedBar />
                          <ConfirmModalInPage />
                          <ExpertHelpPost />
                          {UserInfoStore.data.show_onboarding ? (
                            <Onboading />
                          ) : shouldForcePlanPage ? (
                            UserInfoStore.dataLoadedTime !== 0 ? (
                              <Plan isSubscribeGuide />
                            ) : (
                              <FullScreenLoading />
                            )
                          ) : (
                            <>
                              {!isFullscreen && <V6TrialEndBanner />}
                              {renderRoutes(routes)}
                            </>
                          )}
                        </div>
                      </Suspense>
                      {/* 渲染 Toast，需置于 Frame 内，否则 useFrame 会抛错白屏 */}
                      {CommonStore.toastInfo && (
                        <PolarisToast {...CommonStore.toastInfo!} />
                      )}
                      {/* 渲染 Loading，需置于 Frame 内 */}
                      {!isShopifyEmbedded() &&
                        !isShopifyMobile() &&
                        CommonStore.globalLoading && <Loading />}
                    </Frame>
                  </div>
                )}
              </PBProvide>
            </>
          )}
        </Observer>
      </Provider>
      <PostLayer />
      <ClickToComponent editor={"cursor"} />
    </AppProvider>
  )
}

export default observer(BridgeTransfer)
